import { Star } from "lucide-react";

/**
 * Static star display. Renders 5 stars filled proportionally to ``value``
 * (e.g. 4.3 = 4 full + 0.3 partial). Used on shop cards, product detail
 * summary header, and inside individual review rows.
 */
export function StarRatingDisplay({ value = 0, size = 16, className = "", testId }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <div className={`inline-flex items-center gap-0.5 ${className}`} data-testid={testId} aria-label={`Note ${v.toFixed(1)} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => {
        const fill = Math.max(0, Math.min(1, v - (i - 1)));
        return (
          <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
            <Star className="absolute inset-0 text-[#E5EAE5]" style={{ width: size, height: size }} fill="currentColor" strokeWidth={0} />
            <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
              <Star className="text-[#FACC15]" style={{ width: size, height: size }} fill="currentColor" strokeWidth={0} />
            </span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Interactive 1..5 star input. Hover preview + click-to-set + clear-on-zero.
 */
export function StarRatingInput({ value, onChange, size = 28, testId = "rating-input" }) {
  const v = Number(value) || 0;
  return (
    <div className="inline-flex items-center gap-1" role="radiogroup" aria-label="Note" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          aria-label={`${i} étoile${i > 1 ? "s" : ""}`}
          aria-checked={v === i}
          role="radio"
          data-testid={`${testId}-star-${i}`}
          onClick={() => onChange(i)}
          className="text-[#E5EAE5] hover:text-[#FACC15] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7AC143] rounded-sm"
        >
          <Star
            className={i <= v ? "text-[#FACC15]" : ""}
            style={{ width: size, height: size }}
            fill={i <= v ? "currentColor" : "none"}
            strokeWidth={i <= v ? 0 : 1.5}
          />
        </button>
      ))}
      <span className="text-sm text-[#4B634B] ml-2 min-w-[2ch]" data-testid={`${testId}-value`}>{v ? `${v}/5` : ""}</span>
    </div>
  );
}
